<template>
  <el-select
    :model-value="modelValue"
    :size="sizeMap[props.size]"
    :class="[
      style.select,
      border ? '' : style.noborder,
      `select-${props.size}`,
    ]"
    :popper-class="`${style.popper} select-popper-${props.size}`"
    :clear-icon="CircleCloseFilled"
    :suffix-icon="CaretBottom"
    filterable
    :filter-method="filterMethod"
    :placeholder="placeholder"
    :clearable="clearable"
    :disabled="disabled"
    :multiple="multiple"
    :collapse-tags="collapseTags"
    :collapse-tags-tooltip="collapseTagsTooltip"
    :remote="remote"
    :remote-method="remoteMethod"
    :loading="loading"
    @update:model-value="updateValue"
    @change="handleChange"
    @visible-change="visibleChange"
    @remove-tag="handleRemoveTag"
    @clear="handleClear"
    @blur="handleBlur"
    @focus="handleFocus"
  >
    <template v-if="slots.default" #default>
      <slot />
    </template>
    <template v-else #default>
      <el-option
        v-for="item in filteredOptions"
        :key="item.value"
        :label="item.label"
        :value="item.value"
        :disabled="item.disabled"
      />
    </template>
  </el-select>
</template>

<script setup lang="ts">
import { ref, computed, reactive, useSlots, watch } from 'vue';
import CaretBottom from './CaretBottom.vue';
import CircleCloseFilled from './CircleCloseFilled.vue';

interface Option {
  value: string | number;
  label: string;
  disabled?: boolean;
}

const emit = defineEmits([
  'update:modelValue',
  'change',
  'visible-change',
  'remove-tag',
  'clear',
  'blur',
  'focus',
  'remote-search'
]);

const slots = useSlots();

const props = defineProps({
  modelValue: {
    type: [String, Number, Array],
    default: ''
  },
  size: {
    validator(value: string) {
      return ['max', 'large', 'default', 'small', 'mini'].indexOf(value) > -1;
    },
    type: String,
    default: 'default'
  },
  border: {
    type: Boolean,
    default: true
  },
  options: {
    type: Array<Option>,
    default: () => []
  },
  placeholder: {
    type: String,
    default: '请选择'
  },
  clearable: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  multiple: {
    type: Boolean,
    default: false
  },
  collapseTags: {
    type: Boolean,
    default: false
  },
  collapseTagsTooltip: {
    type: Boolean,
    default: false
  },
  remote: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  }
});

const sizeMap: any = reactive({
  max: 'large',
  large: 'large',
  default: 'default',
  small: 'small',
  mini: 'small'
});

const keyword = ref('');
const localValue = ref(props.modelValue);

// 监听modelValue变化
watch(() => props.modelValue, newVal => {
  localValue.value = newVal;
});

// 更新model值
const updateValue = (value: any) => {
  emit('update:modelValue', value);
};

// 过滤选项的计算属性
const filteredOptions = computed(() => {
  if (!keyword.value || props.remote) {
    return props.options;
  }
  return props.options.filter((item: Option) =>
    item.label.toString().toLowerCase().includes(keyword.value.toLowerCase())
  );
});

// 本地过滤方法
const filterMethod = (val: string) => {
  keyword.value = val;
  return true; // 返回true，让组件显示所有选项，我们通过filteredOptions来控制
};

// 远程搜索方法
const remoteMethod = (val: string) => {
  keyword.value = val;
  emit('remote-search', val);
};

// 选择变化处理
const handleChange = (value: any) => {
  emit('change', value);
};

// 下拉框显示状态变化
const visibleChange = (visible: boolean) => {
  emit('visible-change', visible);
  if (!visible) {
    keyword.value = '';
  }
};

// 移除标签（多选模式）
const handleRemoveTag = (tag: any) => {
  emit('remove-tag', tag);
};

// 清空选择
const handleClear = () => {
  emit('clear');
};

// 失去焦点
const handleBlur = (event: Event) => {
  emit('blur', event);
};

// 获得焦点
const handleFocus = (event: Event) => {
  emit('focus', event);
};
</script>

<style lang="scss" module="style">
.select {
  --el-text-color-regular: var(--text-text1);
  --el-fill-color-blank: var(--base-base1);
  --el-border-color: var(--border-border1);
  &:hover {
    --el-select-border-color-hover: var(--gray-gray10);
  }

  :global(.el-input__wrapper) {
    --el-select-input-focus-border-color: var(--gray-gray10);
    --el-input-border-color: var(--border-border1);
  }

  :global(.el-input__inner) {
    font-size: var(--font-size);
    font-weight: var(--font-weight);
  }

  :global(.el-input__inner::placeholder) {
    color: var(--text-text4);
  }

  &:global {
    &:not(.select-clear-hover) .el-input .el-select__caret.el-icon {
      background: none !important;
    }
    &.el-select:not(.select-clear-hover)
      .el-input
      .el-select__caret.el-icon
      svg {
      display: block;
      color: var(--text-text1);
      font-size: var(--font-size);
      font-weight: var(--font-weight);
    }
  }
}

.noborder {
  :global(.el-input__wrapper) {
    --el-select-input-focus-border-color: none;
    box-shadow: none !important;
  }
}

:global {
  .select {
    &-max {
      --el-border-radius-base: var(--radius-max);
      height: var(--height-max);

      .el-input__inner,
      .el-input__suffix {
        --el-input-height: var(--height-max);
        --font-size: var(--font-size-max);
        --font-weight: var(--font-weight-max);
      }
    }

    &-large {
      --el-border-radius-base: var(--radius-large);
      height: var(--height-large);

      .el-input__inner,
      .el-input__suffix {
        --el-input-height: var(--height-large);
        --font-size: var(--font-size-large);
        --font-weight: var(--font-weight-large);
      }
    }

    &-default {
      --el-border-radius-base: var(--radius-default);
      height: var(--height-default);

      .el-input__inner,
      .el-input__suffix {
        --el-input-height: var(--height-default);
        --font-size: var(--font-size-default);
        --font-weight: var(--font-weight-default);
      }
    }

    &-small {
      --el-border-radius-base: var(--radius-small);
      height: var(--height-small);

      .el-input__inner,
      .el-input__suffix {
        --el-input-height: var(--height-small);
        --font-size: var(--font-size-small);
        --font-weight: var(--font-weight-small);
      }
    }
    &-mini {
      --el-border-radius-base: var(--radius-mini);
      height: var(--height-mini);

      .el-input__inner,
      .el-input__suffix {
        --el-input-height: var(--height-mini);
        --font-size: var(--font-size-mini);
        --font-weight: var(--font-weight-mini);
      }
    }
  }
  .select-popper {
    &-max {
      --el-popper-border-radius: var(--radius-max);
    }

    &-large {
      --el-popper-border-radius: var(--radius-large);
    }

    &-default {
      --el-popper-border-radius: var(--radius-default);
    }

    &-small {
      --el-popper-border-radius: var(--radius-small);
    }
    &-mini {
      --el-popper-border-radius: var(--radius-mini);
    }
  }
}

.popper {
  &:global(.el-select__popper) {
    margin-top: -10px !important;
    border-color: var(--border-border1);
    overflow: hidden;
  }
  :global {
    .el-select-dropdown__item {
      color: var(--text-text2);
    }
    .el-select-dropdown__item.selected {
      color: var(--text-text1) !important;
    }
    .el-select-dropdown__item.hover {
      background-color: var(--base-base2);
    }
    .el-input__suffix {
      display: flex;
      align-items: center;
    }
  }
}
</style>
